import React, { useMemo, useSyncExternalStore } from 'react';
import { Text, TouchableOpacity, View, StyleSheet } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme } from '../../hooks/useTheme';
import { navigationRef } from '../../navigation/navigationRef';
import {
  getVisitPanelProject,
  getVisitRecorderState,
  subscribeVisitPanel,
  subscribeVisitRecorder,
} from '../../services/visitRecorder';
import { formatElapsed } from './helpers';

/**
 * A visit is being recorded and its panel isn't on screen (another screen, or
 * another plan's chat): a red pill at the top of the app that says so and
 * goes back to the recording's plan chat, where Pause and Stop are. The
 * microphone never runs without something on screen saying it is.
 */
export default function RecordingIndicator() {
  const theme = useTheme();
  const insets = useSafeAreaInsets();
  const recorder = useSyncExternalStore(subscribeVisitRecorder, getVisitRecorderState, getVisitRecorderState);
  const panelProject = useSyncExternalStore(subscribeVisitPanel, getVisitPanelProject, getVisitPanelProject);
  const styles = useMemo(() => StyleSheet.create({
    wrap: { position: 'absolute', left: 0, right: 0, alignItems: 'center', zIndex: 1000 },
    pill: {
      flexDirection: 'row', alignItems: 'center', gap: 8, paddingHorizontal: 14, paddingVertical: 8, borderRadius: 18,
      backgroundColor: theme.colors.error, shadowColor: '#000', shadowOpacity: 0.2, shadowRadius: 6, shadowOffset: { width: 0, height: 2 },
    },
    dot: { width: 8, height: 8, borderRadius: 4, backgroundColor: '#fff' },
    text: { color: '#fff', fontSize: 14, fontWeight: '700', fontVariant: ['tabular-nums'] },
  }), [theme]);

  const live = recorder.status === 'recording' || recorder.status === 'paused';
  if (!live || recorder.projectId == null || panelProject === recorder.projectId) return null;
  const label = recorder.status === 'paused' ? 'Recording paused' : 'Recording';
  return (
    <View style={[styles.wrap, { top: insets.top + 6 }]} pointerEvents="box-none">
      <TouchableOpacity
        style={styles.pill}
        testID="visit-recording-indicator"
        accessibilityRole="button"
        accessibilityLabel={`${label}, ${formatElapsed(recorder.elapsedMs)}. Go to the recording`}
        onPress={() => {
          try {
            (navigationRef as any).navigate('Main', { screen: 'Plans', params: { screen: 'ProjectChat', params: { projectId: recorder.projectId } } });
          } catch { /* navigator not ready */ }
        }}
      >
        <View style={styles.dot} />
        <Text style={styles.text}>{`${label} · ${formatElapsed(recorder.elapsedMs)}`}</Text>
      </TouchableOpacity>
    </View>
  );
}
